import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { TRANSLATIONS, DEFAULT_LANG, LANGUAGES, format } from './translations';

const LanguageContext = createContext(null);
const STORAGE_KEY = 'hoc.lang';

function detectInitial() {
  if (typeof window === 'undefined') return DEFAULT_LANG;
  const saved = window.localStorage.getItem(STORAGE_KEY);
  if (saved && TRANSLATIONS[saved]) return saved;
  const nav = ((window.navigator && window.navigator.language) || '').toLowerCase();
  if (nav.startsWith('ko')) return 'ko';
  if (nav.startsWith('id')) return 'id';
  if (nav.startsWith('pt')) return 'pt-BR';
  if (nav.startsWith('th')) return 'th';
  if (nav.startsWith('fil') || nav.startsWith('tl')) return 'fil';
  if (nav.startsWith('zh')) return 'zh';
  if (nav.startsWith('es')) return 'es';
  if (nav.startsWith('ro')) return 'ro';
  if (nav.startsWith('it')) return 'it';
  return DEFAULT_LANG;
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(detectInitial);

  useEffect(() => {
    try { window.localStorage.setItem(STORAGE_KEY, lang); } catch (e) { /* ignore */ }
    if (typeof document !== 'undefined') document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const value = useMemo(() => {
    const dict = TRANSLATIONS[lang] || TRANSLATIONS[DEFAULT_LANG];
    const t = (key, vars) => {
      const parts = key.split('.');
      let node = dict;
      for (const p of parts) {
        if (node && typeof node === 'object' && p in node) node = node[p];
        else { node = null; break; }
      }
      if (node == null) {
        // fallback to english
        node = TRANSLATIONS[DEFAULT_LANG];
        for (const p of parts) {
          if (node && typeof node === 'object' && p in node) node = node[p];
          else { node = key; break; }
        }
      }
      if (typeof node !== 'string') return key;
      return vars ? format(node, vars) : node;
    };
    return { lang, setLang, t, languages: LANGUAGES };
  }, [lang]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useT must be used inside <LanguageProvider>');
  return ctx;
}
